import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, Divider, Select, Table } from "antd";
import config from "../../../config";
import authHeader from "../../Authentication/authHeader";

export default function CustomerBalanceSummary({ tabChange }) {
  const { Option } = Select;
  const [customerLedgers, setCustomerLedgers] = useState([]);
  const [corporateLedgers, setCorporateLedgers] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [centerList, setCenterList] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState("");

  useEffect(() => {
    fetchCenterList();
  }, []);

  const fetchCenterList = async () => {
    try {
      const response = await axios.get(
        `${config.baseUrl}/inventory/center_list/`,
        {
          headers: authHeader(),
        }
      );
      const resData = Array.isArray(response?.data) ? response?.data : [];
      setCenterList(resData);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const fetchCustomerData = async () => {
    try {
      setSalesLoading(true);
      const response = await axios.get(
        `${config.baseUrl}/inventory/customer_ledger_consolidated_list/`,
        {
          headers: authHeader(),
          params: {
            exclude_corporate: true,
            center_id: selectedCenter,
          },
        }
      );
      const resData = Array.isArray(response?.data) ? response?.data : [];
      setCustomerLedgers(resData);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSalesLoading(false);
    }
  };
  const fetchCorporateData = async () => {
    try {
      setSalesLoading(true);
      const response = await axios.get(
        `${config.baseUrl}/inventory/customer_ledger_consolidated_list/`,
        {
          headers: authHeader(),
          params: {
            exclude_corporate: false,
          },
        }
      );
      const resData = Array.isArray(response?.data) ? response?.data : [];
      setCorporateLedgers(resData);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSalesLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerData();
    fetchCorporateData();
  }, [tabChange, selectedCenter]);

  const customerLedgerColumns = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
    },

    {
      title: "Balance",
      render: (record) => {
        if (
          record?.last_balance === undefined ||
          record?.last_balance === null
        ) {
          return "0.00";
        }

        const balance = Number(record.last_balance.toFixed(2));
        const formatted = Math.abs(balance).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        return balance >= 0 ? `${formatted} Dr` : `${formatted} Cr`;
      },
    },
  ];
  const corporateLedgerColumns = [
    {
      title: "Corporate Name",
      dataIndex: "customer_name",
    },

    {
      title: "Balance",
      render: (record) => {
        if (
          record?.last_balance === undefined ||
          record?.last_balance === null
        ) {
          return "0.00";
        }

        const balance = Number(record.last_balance.toFixed(2));
        const formatted = Math.abs(balance).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        return balance >= 0 ? `${formatted} Dr` : `${formatted} Cr`;
      },
    },
  ];

  const totalCorporateBalance = corporateLedgers.reduce(
    (acc, cur) => acc + Number(cur.last_balance || 0),
    0
  );

  const totalCustomerBalance = customerLedgers.reduce(
    (acc, cur) => acc + Number(cur.last_balance || 0),
    0
  );

  return (
    <>
      <div className="farmerDashboard">
        <Select
          className="ms-3 mt-3"
          placeholder="Select Center"
          style={{ width: "200px" }}
          onChange={(value) => setSelectedCenter(value)}
          allowClear
          showSearch
          optionFilterProp="children"
        >
          {centerList?.map((center) => (
            <Option
              value={center?.center?.employee_id}
              key={center?.center?.employee_id}
            >
              {center?.center_name}
            </Option>
          ))}
        </Select>

        <div className="shadow-sm p-3 d-flex justify-content-between">
          <div style={{ width: "49%" }}>
            <p className="text-center" style={{ fontWeight: "bolder" }}>
              Customer
            </p>
            <Table
              columns={customerLedgerColumns}
              dataSource={customerLedgers}
              loading={salesLoading}
              rowKey={(record) => record?.pl_id}
              size="small"
              pagination={false}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={1}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={1}>
                    <strong>
                      {totalCustomerBalance > 0
                        ? `${Number(totalCustomerBalance).toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )} Dr`
                        : `${Number(
                            Math.abs(totalCustomerBalance)
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} Cr`}
                    </strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>
          <div className="border"></div>

          <div style={{ width: "49%" }}>
            <p className="text-center" style={{ fontWeight: "bolder" }}>
              Corporate
            </p>
            <Table
              columns={corporateLedgerColumns}
              dataSource={corporateLedgers}
              loading={salesLoading}
              rowKey={(record) => record?.pl_id}
              size="small"
              pagination={false}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={1}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={1}>
                    <strong>
                      {totalCorporateBalance > 0
                        ? `${Number(totalCorporateBalance).toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )} Dr`
                        : `${Number(
                            Math.abs(totalCorporateBalance)
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} Cr`}
                    </strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>
        </div>
      </div>
    </>
  );
}
